import { z } from 'zod';

/**
 * Brazilian phone normalization to E.164 format.
 *
 * Accepted inputs (examples):
 *   "11 99999-1234"       -> "+5511999991234"
 *   "(11) 9 9999-1234"    -> "+5511999991234"
 *   "11999991234"         -> "+5511999991234"
 *   "+55 11 99999 1234"   -> "+5511999991234"
 *   "(11) 3333-4444"      -> "+551133334444" (landline, allowed but no SMS use)
 *
 * Rejected: numbers without country/area code that we cannot disambiguate, or
 * cell numbers without the leading 9 (post-2014 standard).
 *
 * Mobile detection: after stripping country code, the number is 11 digits and
 * the third digit (after the 2-digit DDD) is 9.
 */

const BR_DDDS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43,
  44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77,
  79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

const stripNonDigits = (raw: string): string => raw.replace(/\D/g, '');

/**
 * Returns the digits-only national number (10 or 11 digits) or null if input cannot
 * be reduced to a valid Brazilian number.
 */
const toNationalDigits = (raw: string): string | null => {
  const digits = stripNonDigits(raw);
  let national = digits;
  if (national.startsWith('55') && (national.length === 12 || national.length === 13)) {
    national = national.slice(2);
  } else if (national.startsWith('0055') && (national.length === 14 || national.length === 15)) {
    national = national.slice(4);
  }
  if (national.length !== 10 && national.length !== 11) return null;
  const ddd = Number.parseInt(national.slice(0, 2), 10);
  if (!BR_DDDS.has(ddd)) return null;
  return national;
};

export const normalizeBrPhone = (raw: string): string | null => {
  const national = toNationalDigits(raw);
  if (national === null) return null;
  return `+55${national}`;
};

export const isMobilePhone = (e164: string): boolean => {
  if (!e164.startsWith('+55')) return false;
  const national = e164.slice(3);
  return national.length === 11 && national[2] === '9';
};

/** Display mask: (11) 98888-7777. Extra digits are dropped. */
export const formatBrPhone = (input: string): string => {
  let digits = stripNonDigits(input);
  if (digits.startsWith('55') && digits.length > 11) digits = digits.slice(2);
  digits = digits.slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length < 3) return `(${digits}`;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  if (digits.length < 11) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
};

export const phoneSchema = z
  .string()
  .min(8, 'phone is too short')
  .max(20, 'phone is too long')
  .transform((raw, ctx) => {
    const e164 = normalizeBrPhone(raw);
    if (e164 === null) {
      ctx.addIssue({
        code: 'custom',
        message: 'invalid Brazilian phone (expected DDD + number)',
      });
      return z.NEVER;
    }
    return e164;
  });

/**
 * Stricter schema for SMS: only mobile numbers accepted.
 */
export const mobilePhoneSchema = phoneSchema.refine(isMobilePhone, {
  message: 'phone must be a mobile number (11 digits with leading 9 after DDD)',
});

export type Phone = z.infer<typeof phoneSchema>;
